import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogOut, CalendarDays, List, CalendarRange, Plus, CalendarClock, HelpCircle, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { CalendarView } from '../components/CalendarView';
import { WeekView } from '../components/WeekView';
import { ProgressTracker, MilestonesSection, StartDateSection, TaskListView, ExpirationWarning, SecurityQuestionBanner, ExpiredScreen } from '../components/user/TaskSections';
import { JournalSection } from '../components/user/JournalSection';
import { EditTaskDialog, StartDateDialog, DayTasksDialog, CreateTaskDialog, SecurityQuestionDialog } from '../components/user/UserDialogs';
import { HelpDialog } from '../components/user/HelpDialog';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const UserDashboard = () => {
  const { user, logout, isImpersonating, impersonatingUser, exitImpersonation } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [showStartDateDialog, setShowStartDateDialog] = useState(false);
  const [editingPlanForStartDate, setEditingPlanForStartDate] = useState(null);
  const [filter, setFilter] = useState('all');
  const [editTask, setEditTask] = useState(null);
  const [editDueDate, setEditDueDate] = useState('');
  const [editHoursLog, setEditHoursLog] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [planInfos, setPlanInfos] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [headerSettings, setHeaderSettings] = useState(null);
  const [showCreateTaskDialog, setShowCreateTaskDialog] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({ activity: '', content: '', goals: '', due_date: '', days_offset: 0, study_plan_template_id: '' });
  const [showSecuritySetup, setShowSecuritySetup] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('taskSortBy') || 'due_date');
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('taskSortOrder') || 'asc');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDayTasks, setSelectedDayTasks] = useState([]);
  const [showDayTasksDialog, setShowDayTasksDialog] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff));
  });

  // --- Data fetching ---
  const fetchHeaderSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/header`);
      if (data && (data.logo_filename || data.logo_bg_color || data.text_color)) {
        setHeaderSettings(data);
      }
    } catch { /* header settings are optional */ }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      const assignedPlans = data.assigned_plans || [];
      if (assignedPlans.length > 0) {
        try {
          const planRes = await axios.get(`${BACKEND_URL}/api/user/study-plan-info`, { withCredentials: true });
          setPlanInfos(Array.isArray(planRes.data) ? planRes.data : []);
        } catch { /* plan info optional */ }
      }
    } catch { /* auth check handled elsewhere */ }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/user/study-plan`, { withCredentials: true });
      setTasks(data);
    } catch { /* tasks fetch failed */ }
    setLoading(false);
  }, []);

  const fetchMilestones = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/user/milestones`, { withCredentials: true });
      setMilestones(data);
    } catch { /* milestones optional */ }
  }, []);

  const fetchJournal = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/user/journal`, { withCredentials: true });
      setJournalEntries(data);
    } catch { /* journal optional */ }
  }, []);

  const fetchSecurityQuestions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/security-questions`);
      setSecurityQuestions(data);
    } catch { /* security questions optional */ }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchCurrentUser();
    fetchMilestones();
    fetchHeaderSettings();
    fetchSecurityQuestions();
    fetchJournal();
  }, [fetchTasks, fetchCurrentUser, fetchMilestones, fetchHeaderSettings, fetchSecurityQuestions, fetchJournal]);

  // --- Handlers ---

  const handleCreateJournal = async (title, content) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/user/journal`, { title, content }, { withCredentials: true });
    setJournalEntries(prev => [data, ...prev]);
    toast.success('Journal entry saved');
  };

  const handleUpdateJournal = async (id, title, content) => {
    const { data } = await axios.put(`${BACKEND_URL}/api/user/journal/${id}`, { title, content }, { withCredentials: true });
    setJournalEntries(prev => prev.map(e => e.id === id ? data : e));
    toast.success('Journal entry updated');
  };

  const handleDeleteJournal = async (id) => {
    await axios.delete(`${BACKEND_URL}/api/user/journal/${id}`, { withCredentials: true });
    setJournalEntries(prev => prev.filter(e => e.id !== id));
    toast.success('Journal entry deleted');
  };

  const handleDismissWelcome = async (planId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/user/dismiss-welcome-note/${planId}`, {}, { withCredentials: true });
      setPlanInfos(prev => prev.map(p => p.plan_id === planId ? { ...p, welcome_notes: '' } : p));
      toast.success('Welcome note dismissed');
    } catch { /* silent */ }
  };

  const handleSetupSecurityQuestion = async () => {
    if (!selectedQuestion || !securityAnswer.trim()) {
      toast.error('Please select a question and provide an answer');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/auth/setup-security-question`, { security_question: selectedQuestion, security_answer: securityAnswer }, { withCredentials: true });
      toast.success('Security question set! You can now reset your password from the login page.');
      setShowSecuritySetup(false);
      setSelectedQuestion('');
      setSecurityAnswer('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set security question');
    }
  };

  const handleStartDateUpdate = async () => {
    if (!startDate) { toast.error('Please select a start date'); return; }
    if (!editingPlanForStartDate) { toast.error('No plan selected'); return; }
    try {
      await axios.put(`${BACKEND_URL}/api/user/study-plan/${editingPlanForStartDate.plan_id}/start-date`, { start_date: startDate }, { withCredentials: true });
      toast.success('Start date updated! All due dates recalculated.');
      setShowStartDateDialog(false);
      fetchTasks();
      fetchCurrentUser();
    } catch (error) {
      toast.error(`Failed to update start date: ${error.response?.data?.detail || error.message}`);
    }
  };

  const openStartDateDialog = (planInfo) => {
    setEditingPlanForStartDate(planInfo);
    setStartDate(planInfo?.start_date || '');
    setShowStartDateDialog(true);
  };

  const handleEditTask = (task) => {
    setEditTask(task);
    setEditDueDate(task.due_date || '');
    setEditHoursLog(task.hours_log?.toString() || '0');
    setEditStatus(task.status);
  };

  const handleSaveTask = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/user/study-plan/tasks/${editTask.id}`, { due_date: editDueDate || null, hours_log: parseFloat(editHoursLog) || 0, status: editStatus }, { withCredentials: true });
      toast.success('Task updated successfully!');
      setEditTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(`Failed to update task: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCreateCustomTask = async () => {
    if (!newTaskForm.activity.trim()) { toast.error('Activity name is required'); return; }
    const body = {
      activity: newTaskForm.activity,
      content: newTaskForm.content,
      goals: newTaskForm.goals,
      due_date: newTaskForm.due_date || null,
      days_offset: parseInt(newTaskForm.days_offset) || 0
    };
    if (newTaskForm.study_plan_template_id) {
      body.study_plan_template_id = newTaskForm.study_plan_template_id;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/user/study-plan/tasks`, body, { withCredentials: true });
      toast.success('Custom task added!');
      setShowCreateTaskDialog(false);
      setNewTaskForm({ activity: '', content: '', goals: '', due_date: '', days_offset: 0, study_plan_template_id: '' });
      fetchTasks();
    } catch (error) {
      toast.error(`Failed to create task: ${error.response?.data?.detail || error.message}`);
    }
  };

  // --- Computed ---
  const filteredTasks = tasks.filter(task => filter === 'all' || task.status === filter);

  const taskStats = useMemo(() => {
    const completedCount = tasks.filter(t => t.status === 'complete').length;
    const openCount = tasks.filter(t => t.status === 'open').length;
    const totalHours = tasks.reduce((sum, t) => sum + (t.hours_log || 0), 0);
    const completionPct = tasks.length > 0 ? (completedCount / tasks.length) * 100 : 0;
    return { completedCount, openCount, totalHours, completionPct };
  }, [tasks]);

  const expirationInfo = useMemo(() => {
    const assignedPlans = user?.assigned_plans || [];
    if (assignedPlans.length === 0) return { isExpired: false, daysLeft: null, showWarning: false };
    const plansWithExpiry = assignedPlans.filter(p => p.expiration_date);
    if (plansWithExpiry.length === 0) return { isExpired: false, daysLeft: null, showWarning: false };
    // Parse dates as local time to avoid UTC offset issues
    const parseLocal = (d) => { const p = d.split('-'); return p.length === 3 ? new Date(p[0], p[1]-1, p[2]) : new Date(d+'T00:00:00'); };
    // Check if ALL plans with an expiry are expired
    const now = new Date();
    const allExpired = plansWithExpiry.every(p => parseLocal(p.expiration_date) < now);
    // Find the nearest expiration for warnings
    const nearestExpiry = plansWithExpiry
      .map(p => ({ date: parseLocal(p.expiration_date), raw: p.expiration_date }))
      .sort((a, b) => a.date - b.date)[0];
    const daysLeft = nearestExpiry ? Math.ceil((nearestExpiry.date - now) / (1000 * 60 * 60 * 24)) : null;
    return {
      isExpired: allExpired,
      daysLeft,
      showWarning: daysLeft !== null && daysLeft >= 0 && daysLeft <= 30,
      isUrgent: daysLeft !== null && daysLeft >= 0 && daysLeft <= 7,
      expirationDate: nearestExpiry?.raw
    };
  }, [user?.assigned_plans]);

  const sortedTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      if (sortBy === 'due_date') {
        if (!a.due_date && !b.due_date) return a.activity.localeCompare(b.activity);
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        const dateA = new Date(a.due_date);
        const dateB = new Date(b.due_date);
        const dateCompare = sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        if (dateCompare !== 0) return dateCompare;
        // Same date — sort alphabetically by activity
        return a.activity.localeCompare(b.activity);
      } else if (sortBy === 'status') {
        if (a.status === b.status) return 0;
        return sortOrder === 'asc' ? (a.status === 'open' ? -1 : 1) : (a.status === 'complete' ? -1 : 1);
      } else if (sortBy === 'activity') {
        const nameA = a.activity.toLowerCase();
        const nameB = b.activity.toLowerCase();
        return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      }
      return 0;
    });
  }, [filteredTasks, sortBy, sortOrder]);

  const toggleSort = (column) => {
    if (sortBy === column) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      localStorage.setItem('taskSortOrder', newOrder);
    } else {
      setSortBy(column);
      setSortOrder('asc');
      localStorage.setItem('taskSortBy', column);
      localStorage.setItem('taskSortOrder', 'asc');
    }
  };

  const getTasksByDate = useCallback((date) => {
    const dateStr = date.toISOString().split('T')[0];
    return tasks.filter(task => task.due_date === dateStr);
  }, [tasks]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { daysInMonth: lastDay.getDate(), startingDayOfWeek: firstDay.getDay(), year, month };
  };

  const handleDayClick = (dayTasks) => { if (dayTasks.length > 0) { setSelectedDayTasks(dayTasks); setShowDayTasksDialog(true); } };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const navigateWeek = (direction) => {
    if (direction === 0) {
      const today = new Date();
      const day = today.getDay();
      const diff = today.getDate() - day;
      setCurrentWeekStart(new Date(today.setDate(diff)));
    } else {
      const newWeek = new Date(currentWeekStart);
      newWeek.setDate(newWeek.getDate() + (direction * 7));
      setCurrentWeekStart(newWeek);
    }
  };

  const downloadPDF = async () => {
    // Load logo with rounded corners via Canvas
    let logoDataUrl = null;
    try {
      const logoImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = `${window.location.origin}/pdf-logo.png`;
      });
      const canvas = document.createElement('canvas');
      canvas.width = logoImg.width;
      canvas.height = logoImg.height;
      const ctx = canvas.getContext('2d');
      const r = 24;
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(canvas.width - r, 0);
      ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
      ctx.lineTo(canvas.width, canvas.height - r);
      ctx.quadraticCurveTo(canvas.width, canvas.height, canvas.width - r, canvas.height);
      ctx.lineTo(r, canvas.height);
      ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
      ctx.lineTo(0, r);
      ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(logoImg, 0, 0);
      logoDataUrl = canvas.toDataURL('image/png');
    } catch { /* logo optional */ }

    const doc = new jsPDF({
      encryption: {
        userPassword: '',
        ownerPassword: 'OmniPear2026#SecurePDF',
        permissions: ['print'],
        encryptionType: 128
      }
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const usableWidth = pageWidth - 2 * margin;
    const colGap = 5;
    const cardWidth = (usableWidth - 2 * colGap) / 3;
    const cardPad = 3.5;
    const footerZone = 18;
    const contentBottom = pageHeight - footerZone;
    let currentPage = 1;
    let yPos = margin;

    const blue = [37, 99, 235];
    const darkBlue = [30, 64, 175];
    const lightBlue = [191, 219, 254];
    const darkText = [15, 23, 42];
    const gray = [100, 116, 139];
    const lightBg = [241, 245, 249];
    const green = [22, 163, 74];
    const amber = [217, 119, 6];

    const addFooter = () => {
      const fy = pageHeight - 10;
      doc.setDrawColor(200, 210, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, fy - 4, pageWidth - margin, fy - 4);
      doc.setFontSize(6.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...gray);
      doc.text(`OmniPear StudyPlan Pro - ${user?.name || ''}`, margin, fy);
      doc.text('Content is copyrighted and protected by OmniPear. All rights are reserved and strictly enforced.', pageWidth / 2, fy, { align: 'center' });
      doc.text(`Page ${currentPage}`, pageWidth - margin, fy, { align: 'right' });
    };

    const newPage = () => {
      addFooter();
      doc.addPage();
      currentPage++;
      yPos = margin;
    };

    // === HEADER SECTION ===
    // Top accent bar
    doc.setFillColor(...blue);
    doc.rect(0, 0, pageWidth, 4, 'F');

    // Logo top-right with rounded corners
    const logoW = 48;
    const logoH = 48 * (244 / 636); // maintain aspect ratio ~18.4mm
    const logoX = pageWidth - margin - logoW;
    const logoY = 6;
    if (logoDataUrl) {
      try { doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoW, logoH); } catch { /* logo rendering optional */ }
    }

    yPos = 12;
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...darkBlue);
    doc.text('My Study Plan Details', margin, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...gray);
    doc.text(`Student: ${user?.name || ''}`, margin, yPos);
    yPos += 5;
    planInfos.forEach(p => {
      if (p.start_date) {
        const parts = p.start_date.split('-');
        const localDate = parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : new Date(p.start_date + 'T00:00:00');
        doc.text(`${p.plan_name} Start: ${localDate.toLocaleDateString()}`, margin, yPos);
        yPos += 5;
      }
    });
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPos);
    yPos += 7;

    // Blue divider
    doc.setDrawColor(...blue);
    doc.setLineWidth(0.8);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 6;

    // === PROGRESS SUMMARY BOX ===
    const completedCount = tasks.filter(t => t.status === 'complete').length;
    const totalHours = tasks.reduce((sum, t) => sum + (t.hours_log || 0), 0);
    const completionRate = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    const summaryH = 22;

    doc.setFillColor(...lightBg);
    doc.setDrawColor(...lightBlue);
    doc.setLineWidth(0.4);
    doc.roundedRect(margin, yPos, usableWidth, summaryH, 3, 3, 'FD');

    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...darkBlue);
    doc.text('Progress Summary', margin + 5, yPos + 7);

    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...darkText);
    const sY = yPos + 15;
    const sW = usableWidth / 4;
    doc.text(`Total Tasks: ${tasks.length}`, margin + 5, sY);
    doc.setTextColor(...green);
    doc.text(`Completed: ${completedCount} (${completionRate}%)`, margin + sW, sY);
    doc.setTextColor(...darkText);
    doc.text(`Hours Logged: ${totalHours.toFixed(1)}h`, margin + sW * 2, sY);
    doc.setTextColor(...amber);
    doc.text(`Open: ${tasks.length - completedCount}`, margin + sW * 3, sY);
    doc.setTextColor(...darkText);

    yPos += summaryH + 8;

    // === TASK CARDS HEADING ===
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...darkBlue);
    doc.text('Tasks', margin, yPos);
    yPos += 7;

    // === TASK CARDS IN 3-COLUMN GRID ===
    const maxContentLines = 3;

    // Pre-compute card data
    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    const textMaxW = cardWidth - 2 * cardPad;

    const cardDataAll = tasks.map((task, idx) => {
      doc.setFontSize(7.5);
      doc.setFont(undefined, 'bold');
      const actLines = doc.splitTextToSize(`${idx + 1}. ${task.activity}`, textMaxW);

      doc.setFontSize(6.5);
      doc.setFont(undefined, 'normal');
      let cLines = doc.splitTextToSize(task.content || 'N/A', textMaxW - 1);
      if (cLines.length > maxContentLines) { cLines = cLines.slice(0, maxContentLines); cLines[maxContentLines - 1] += '...'; }
      const gLines = doc.splitTextToSize(task.goals || 'N/A', textMaxW - 1);

      const headerH = 8 + Math.max(0, actLines.length - 1) * 3.5;
      const bodyH = 5 + cLines.length * 3.2 + 4 + gLines.length * 3.2 + 4 + 4 + 4 + 3;
      const totalH = headerH + bodyH + cardPad;

      return { task, idx, actLines, cLines, gLines, headerH, totalH: Math.max(totalH, 48) };
    });

    // Draw cards row by row
    for (let i = 0; i < cardDataAll.length; i += 3) {
      const row = cardDataAll.slice(i, Math.min(i + 3, cardDataAll.length));
      const rowH = Math.max(...row.map(c => c.totalH));

      if (yPos + rowH > contentBottom) { newPage(); }

      row.forEach((card, col) => {
        const cx = margin + col * (cardWidth + colGap);
        const cy = yPos;

        // Card shadow (subtle offset rect)
        doc.setFillColor(230, 233, 240);
        doc.roundedRect(cx + 0.5, cy + 0.5, cardWidth, rowH, 2.5, 2.5, 'F');

        // Card background
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(...lightBlue);
        doc.setLineWidth(0.4);
        doc.roundedRect(cx, cy, cardWidth, rowH, 2.5, 2.5, 'FD');

        // Blue header bar (top rounded, bottom flat)
        doc.setFillColor(...blue);
        doc.roundedRect(cx, cy, cardWidth, card.headerH, 2.5, 2.5, 'F');
        doc.rect(cx, cy + card.headerH - 2.5, cardWidth, 2.5, 'F');

        // Activity name in header
        doc.setFont(undefined, 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(255, 255, 255);
        card.actLines.forEach((line, li) => {
          doc.text(line, cx + cardPad, cy + 5.5 + li * 3.5);
        });

        let fy = cy + card.headerH + 4;

        // Status with colored dot
        const sColor = card.task.status === 'complete' ? green : amber;
        doc.setFillColor(...sColor);
        doc.circle(cx + cardPad + 1.2, fy - 0.8, 1.2, 'F');
        doc.setFont(undefined, 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...darkText);
        doc.text(card.task.status.toUpperCase(), cx + cardPad + 4, fy);
        fy += 5;

        // Content
        doc.setFont(undefined, 'bold');
        doc.setFontSize(6);
        doc.setTextColor(...gray);
        doc.text('Content', cx + cardPad, fy);
        fy += 3;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...darkText);
        card.cLines.forEach(line => { doc.text(line, cx + cardPad, fy); fy += 3.2; });
        fy += 1;

        // Goals
        doc.setFont(undefined, 'bold');
        doc.setFontSize(6);
        doc.setTextColor(...gray);
        doc.text('Goals', cx + cardPad, fy);
        fy += 3;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...darkText);
        card.gLines.forEach(line => { doc.text(line, cx + cardPad, fy); fy += 3.2; });
        fy += 1;

        // Separator line
        doc.setDrawColor(220, 225, 235);
        doc.setLineWidth(0.2);
        doc.line(cx + cardPad, fy, cx + cardWidth - cardPad, fy);
        fy += 3;

        // Due Date & Hours on same area
        doc.setFont(undefined, 'bold');
        doc.setFontSize(6);
        doc.setTextColor(...gray);
        doc.text('Due:', cx + cardPad, fy);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...darkText);
        doc.text(card.task.due_date || 'Not set', cx + cardPad + 8, fy);
        fy += 4;

        doc.setFont(undefined, 'bold');
        doc.setFontSize(6);
        doc.setTextColor(...gray);
        doc.text('Hours:', cx + cardPad, fy);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...darkText);
        doc.text(`${card.task.hours_log || 0}h`, cx + cardPad + 11, fy);
      });

      yPos += rowH + 5;
    }

    // Add footer to last page
    addFooter();

    doc.save(`Study_Plan_${user?.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Study plan downloaded as PDF!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading your study plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {isImpersonating && (
        <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between sticky top-0 z-50" data-testid="impersonation-banner">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            <span className="text-sm font-semibold">Admin Impersonation Mode</span>
            <span className="text-sm">— Viewing as <strong>{impersonatingUser?.name}</strong> ({impersonatingUser?.email})</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="bg-white text-amber-700 hover:bg-amber-50 border-white"
            onClick={exitImpersonation}
            data-testid="exit-impersonation"
          >
            Exit &amp; Return to Admin
          </Button>
        </div>
      )}
      <header
        className={`border-b flex items-center justify-between px-3 sm:px-6 ${isImpersonating ? 'sticky top-[44px]' : 'sticky top-0'} z-30`}
        style={{
          backgroundColor: headerSettings?.logo_bg_color || '#ffffff',
          borderColor: headerSettings?.logo_bg_color ? 'transparent' : '#e2e8f0',
          height: Math.max(56, (headerSettings?.logo_size || 40) + 8)
        }}
        data-testid="user-header"
      >
        <div className="flex items-center gap-3 sm:gap-6 min-w-0">
          {headerSettings?.logo_filename && (
            <img
              src={`${BACKEND_URL}/api/settings/logo?t=${Date.now()}`}
              alt="Logo"
              className="object-contain rounded shrink-0"
              style={{ height: headerSettings?.logo_size || 40, maxWidth: (headerSettings?.logo_size || 40) * 1.2 }}
              data-testid="header-logo"
            />
          )}
          <h1 className="text-lg sm:text-2xl font-bold truncate" style={{ fontFamily: 'Manrope, sans-serif', color: headerSettings?.text_color || '#0f172a' }}>
            StudyPlan Pro
          </h1>
          {planInfos.length > 0 && (
            <span className="text-sm sm:text-lg font-medium truncate hidden sm:inline" style={{ fontFamily: 'Manrope, sans-serif', color: headerSettings?.text_color || '#2563eb' }}>
              - {planInfos.map(p => p.plan_name).join(', ')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <span className="text-xs sm:text-sm hidden sm:inline" style={{ fontFamily: 'Figtree, sans-serif', color: headerSettings?.text_color ? `${headerSettings.text_color}cc` : '#475569' }}>
            Welcome, <strong>{user?.name}</strong>
          </span>
          <Button onClick={() => setShowHelp(true)} variant="ghost" size="sm" className="hover:opacity-80" style={{ color: headerSettings?.text_color || '#475569' }} data-testid="help-button">
            <HelpCircle className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Help</span>
          </Button>
          <Button onClick={logout} variant="ghost" size="sm" className="hover:opacity-80" style={{ color: headerSettings?.text_color || '#475569' }} data-testid="logout-button">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      {expirationInfo.isExpired ? (
        <ExpiredScreen CalendarClockIcon={CalendarClock} />
      ) : (
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto pb-14">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="welcome-name">
            Welcome, {user?.name}
          </h1>

          <ExpirationWarning expirationInfo={expirationInfo} />
          <SecurityQuestionBanner user={user} onSetup={() => setShowSecuritySetup(true)} />
          <ProgressTracker tasks={tasks} taskStats={taskStats} downloadPDF={downloadPDF} />

          {/* Welcome! Section - only shows if plan has welcome notes */}
          {planInfos.filter(p => p.welcome_notes).map((planInfo) => (
            <div key={`welcome-${planInfo.plan_id}`} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 mb-6" data-testid={`welcome-section-${planInfo.plan_id}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Welcome!
                </h2>
                {!isImpersonating && (
                  <div className="flex items-center gap-1">
                    <div className="relative group">
                      <button className="p-1 text-slate-400 hover:text-slate-600" data-testid={`welcome-help-${planInfo.plan_id}`}>
                        <HelpCircle className="w-4 h-4" />
                      </button>
                      <div className="absolute right-0 top-6 w-52 bg-slate-800 text-white text-xs rounded-lg p-2.5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-lg">
                        Dismiss this welcome message. It will also auto-hide after your 5th login.
                      </div>
                    </div>
                    <button
                      onClick={() => handleDismissWelcome(planInfo.plan_id)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                      title="Dismiss welcome note"
                      data-testid={`dismiss-welcome-${planInfo.plan_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }} dangerouslySetInnerHTML={{ __html: planInfo.welcome_notes }} />
            </div>
          ))}

          <MilestonesSection milestones={milestones} planInfos={planInfos} />

          {planInfos.map((planInfo) => (
            <StartDateSection
              key={planInfo.plan_id}
              currentStartDate={planInfo.start_date}
              studyPlanName={planInfo.plan_name}
              expirationDate={planInfo.expiration_date}
              openStartDateDialog={() => openStartDateDialog(planInfo)}
            />
          ))}

          {planInfos.filter(p => p.path_plan).map((planInfo) => (
            <div key={planInfo.plan_id} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 mb-6" data-testid={`path-plan-section-${planInfo.plan_id}`}>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Study Path Plan{planInfos.length > 1 ? ` - ${planInfo.plan_name}` : ''}
              </h2>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" style={{ fontFamily: 'Figtree, sans-serif' }}>{planInfo.path_plan}</p>
            </div>
          ))}

          <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
            <Tabs defaultValue="list" className="w-full">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Study Plan Details</h2>
                  <Button onClick={() => setShowCreateTaskDialog(true)} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-custom-task-button">
                    <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Add Task</span>
                  </Button>
                </div>
                <TabsList className="self-start">
                  <TabsTrigger value="list" data-testid="list-view-tab"><List className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">List</span></TabsTrigger>
                  <TabsTrigger value="week" data-testid="week-view-tab"><CalendarRange className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Week</span></TabsTrigger>
                  <TabsTrigger value="calendar" data-testid="calendar-view-tab"><CalendarDays className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Month</span></TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="list" className="mt-0">
                <TaskListView sortedTasks={sortedTasks} filter={filter} setFilter={setFilter} sortBy={sortBy} sortOrder={sortOrder} toggleSort={toggleSort} handleEditTask={handleEditTask} />
              </TabsContent>
              <TabsContent value="week" className="mt-0">
                <WeekView tasks={tasks} currentWeekStart={currentWeekStart} onNavigateWeek={navigateWeek} getTasksByDate={getTasksByDate} onTaskClick={handleEditTask} />
              </TabsContent>
              <TabsContent value="calendar" className="mt-0">
                <CalendarView tasks={tasks} currentMonth={currentMonth} onDayClick={handleDayClick} onNavigateMonth={(direction) => { direction === 0 ? setCurrentMonth(new Date()) : navigateMonth(direction); }} getDaysInMonth={getDaysInMonth} getTasksByDate={getTasksByDate} />
              </TabsContent>
            </Tabs>
          </div>

          {/* My Journal Section */}
          <JournalSection
            entries={journalEntries}
            onCreateEntry={handleCreateJournal}
            onUpdateEntry={handleUpdateJournal}
            onDeleteEntry={handleDeleteJournal}
            isReadOnly={isImpersonating}
          />
        </main>
      )}

      <EditTaskDialog editTask={editTask} setEditTask={setEditTask} editDueDate={editDueDate} setEditDueDate={setEditDueDate} editHoursLog={editHoursLog} setEditHoursLog={setEditHoursLog} editStatus={editStatus} setEditStatus={setEditStatus} handleSaveTask={handleSaveTask} />
      <StartDateDialog showStartDateDialog={showStartDateDialog} setShowStartDateDialog={setShowStartDateDialog} currentStartDate={editingPlanForStartDate?.start_date} planName={editingPlanForStartDate?.plan_name} startDate={startDate} setStartDate={setStartDate} handleStartDateUpdate={handleStartDateUpdate} />
      <DayTasksDialog showDayTasksDialog={showDayTasksDialog} setShowDayTasksDialog={setShowDayTasksDialog} selectedDayTasks={selectedDayTasks} handleEditTask={handleEditTask} />
      <CreateTaskDialog showCreateTaskDialog={showCreateTaskDialog} setShowCreateTaskDialog={setShowCreateTaskDialog} newTaskForm={newTaskForm} setNewTaskForm={setNewTaskForm} handleCreateCustomTask={handleCreateCustomTask} planInfos={planInfos} />
      <SecurityQuestionDialog showSecuritySetup={showSecuritySetup} setShowSecuritySetup={setShowSecuritySetup} securityQuestions={securityQuestions} selectedQuestion={selectedQuestion} setSelectedQuestion={setSelectedQuestion} securityAnswer={securityAnswer} setSecurityAnswer={setSecurityAnswer} handleSetupSecurityQuestion={handleSetupSecurityQuestion} />
      <HelpDialog showHelp={showHelp} setShowHelp={setShowHelp} />

      <div className="fixed bottom-0 left-0 right-0 z-50 bg-amber-50 border-t border-amber-200 px-4 py-2 text-center text-xs sm:text-sm text-amber-800" data-testid="beta-disclaimer">
        BETA version in progress - If you encounter any technical issues or need help, please contact us at{' '}
        <a href="mailto:service@omnipear.com" className="font-semibold underline hover:text-amber-900">service@omnipear.com</a>
      </div>
    </div>
  );
};
